import { NavLink, useLocation } from "react-router-dom";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Tab, Tabs } from "ui/tabs";

import Icons from "assets";

const builderTabs = [
  { id: "design", label: "Design" },
  { id: "content-settings", label: "Content Settings" },
  { id: "preview", label: "Preview" },
] as const;

export type BuilderTabId = (typeof builderTabs)[number]["id"];
export type BuilderTabName = (typeof builderTabs)[number]["label"];

type BuilderNavTabProps = {
  isPreviewMode: boolean;
  url: string;
  tabValidity: Record<BuilderTabId, boolean>;
  currentTab?: string;
  hiddenTabIds?: readonly BuilderTabId[];
};

/**
 * The strip spans the bar so its rule runs edge to edge. `pt` keeps the bar at the 58px the centred
 * `NavLink`s gave it; a sub-nav tab's only padding is under its label.
 */
const BAR_SX: SxProps<Theme> = (theme) => ({
  px: 3,
  pt: 3.5,
  backgroundColor: theme.palette.grey[100],
});

/** Red in every tab state: the tab's own colour is not inherited. The strip sizes the box. */
const INVALID_SX: SxProps<Theme> = (theme) => ({ display: "flex", color: theme.palette.error.main });

export const BuilderTabNav = ({
  isPreviewMode,
  url,
  tabValidity,
  currentTab,
  hiddenTabIds = [],
  ...rest
}: BuilderNavTabProps) => {
  const location = useLocation();
  const visibleTabs = builderTabs.filter((tab) => !hiddenTabIds.includes(tab.id));
  const selectedTab = visibleTabs.some((tab) => tab.id === currentTab) ? currentTab : false;

  return (
    <Tabs
      appearance="sub-nav"
      size="large"
      value={selectedTab}
      aria-label="Builder"
      data-testid="builder-tab-nav"
      sx={BAR_SX}
      {...rest}>
      {visibleTabs.map((tab) =>
        isPreviewMode && tab.id === "design" ? (
          <Tab
            key={tab.id}
            value={tab.id}
            label={tab.label}
            startIcon={<Icons.LockClosed />}
            disabled
          />
        ) : (
          <Tab
            key={tab.id}
            value={tab.id}
            component={NavLink}
            to={url.replace(/\/[^/]*$/, `/${tab.id}`)}
            state={location.state}
            label={tab.label}
            endIcon={
              tabValidity[tab.id] ? undefined : (
                <Box component="span" data-testid={`builder-tab-warning-${tab.id}`} sx={INVALID_SX}>
                  <Icons.InfoIndicator />
                </Box>
              )
            }
          />
        ),
      )}
    </Tabs>
  );
};
